import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Layers,
  Package,
  DollarSign,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Wallet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const availableTreasury = (stats?.total_cash_advances || 0) - (stats?.total_inventory_value || 0);
  const treasuryIsPositive = availableTreasury >= 0;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, alertsRes, activityRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/alerts`),
        axios.get(`${API}/dashboard/recent-activity?limit=10`)
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'stock_in':
        return <ArrowUpRight className="w-4 h-4 text-green-500" />;
      case 'stock_out':
        return <ArrowDownRight className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Stock management overview</p>
      </div>

      <div className="page-content">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card className="rounded-none border-zinc-200" data-testid="stat-components">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Components</p>
                  <p className="text-3xl font-semibold font-mono mt-1">{stats?.total_components || 0}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Box className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="stat-sub-assemblies">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Sub-Assemblies</p>
                  <p className="text-3xl font-semibold font-mono mt-1">{stats?.total_sub_assemblies || 0}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Layers className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="stat-assemblies">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Assemblies</p>
                  <p className="text-3xl font-semibold font-mono mt-1">{stats?.total_assemblies || 0}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="stat-inventory-value">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Inventory Value</p>
                  <p className="text-2xl font-semibold font-mono mt-1">{formatCurrency(stats?.total_inventory_value)}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className={`rounded-none cursor-pointer transition-colors ${
              treasuryIsPositive
                ? "border-green-200 bg-green-50/30 hover:bg-green-50"
                : "border-red-200 bg-red-50/30 hover:bg-red-50"
            }`}
            data-testid="stat-cash-advances"
            onClick={() => navigate('/cash-advances')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Trésorerie disponible</p>
                  <p className={`text-2xl font-semibold font-mono mt-1 ${treasuryIsPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(availableTreasury)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Inventaire - Avances de trésorerie</p>
                </div>
                <div className={`w-12 h-12 flex items-center justify-center ${treasuryIsPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Wallet className={`w-6 h-6 ${treasuryIsPositive ? 'text-green-600' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts and Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Low Stock Alerts */}
          <Card className="rounded-none border-zinc-200" data-testid="low-stock-alerts">
            <CardHeader className="border-b border-zinc-100 bg-zinc-50/50 py-4">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                Low Stock Alerts
                {alerts.length > 0 && (
                  <Badge variant="destructive" className="ml-2 rounded-none">
                    {alerts.length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[320px]">
                {alerts.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    No low stock alerts
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {alerts.map((alert) => (
                      <div key={alert.id} className="flex items-center gap-4 p-4 hover:bg-zinc-50/50 transition-colors" data-testid={`alert-${alert.id}`}>
                        <div className={`w-2 h-2 rounded-full ${alert.status === 'critical' ? 'bg-red-500' : 'bg-orange-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{alert.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{alert.sku}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-mono font-semibold ${alert.status === 'critical' ? 'text-red-500' : 'text-orange-500'}`}>
                            {alert.current_quantity}
                          </p>
                          <p className="text-xs text-muted-foreground">min: {alert.min_stock_level}</p>
                        </div>
                        <Badge variant="outline" className="rounded-none text-xs uppercase">
                          {alert.item_type.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="rounded-none border-zinc-200" data-testid="recent-activity">
            <CardHeader className="border-b border-zinc-100 bg-zinc-50/50 py-4">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[320px]">
                {activity.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    No recent activity
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {activity.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-zinc-50/50 transition-colors" data-testid={`activity-${item.id}`}>
                        {getTransactionIcon(item.transaction_type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-mono font-semibold ${item.transaction_type === 'stock_in' ? 'text-green-600' :
                            item.transaction_type === 'stock_out' ? 'text-red-600' : 'text-blue-600'
                            }`}>
                            {item.transaction_type === 'stock_in' ? '+' : item.transaction_type === 'stock_out' ? '-' : ''}
                            {item.quantity}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase">{item.transaction_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
